type ErrorPayload = Record<string, unknown>;

const MAX_REMOVE_SUPPLIER_DETAIL_LENGTH = 500;

type AxiosLike = {
  response?: { status?: number; data?: unknown };
  status?: number;
  data?: unknown;
  message?: string;
};

function extractFromRecord(data: ErrorPayload | undefined): string {
  if (!data) {
    return '';
  }
  const { error_message: errorMessage, error } = data;
  if (typeof errorMessage === 'string' && errorMessage.trim()) {
    return errorMessage.trim();
  }
  if (typeof error === 'string' && error.trim()) {
    return error.trim();
  }
  return '';
}

function stringifyBody(data: unknown): string {
  if (data === undefined || data === null) {
    return '';
  }
  if (typeof data === 'string') {
    return data.trim();
  }
  try {
    return JSON.stringify(data);
  } catch {
    return String(data);
  }
}

function truncateDetail(text: string, maxLength = MAX_REMOVE_SUPPLIER_DETAIL_LENGTH): string {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength)}…`;
}

function formatHttpStatusAndBody(status: number | undefined, body: unknown): string {
  const bodyText = stringifyBody(body);
  if (status !== undefined) {
    return bodyText ? `${status} ${bodyText}` : String(status);
  }
  return bodyText;
}

function asErrorPayload(data: unknown): ErrorPayload | undefined {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as ErrorPayload;
  }
  return undefined;
}

function readAxiosLike(source: unknown): { status?: number; data?: unknown; message?: string } {
  if (!source || typeof source !== 'object') {
    return {};
  }
  const axiosLike = source as AxiosLike;
  return {
    status: axiosLike.response?.status ?? axiosLike.status,
    data: axiosLike.response?.data ?? axiosLike.data,
    message: axiosLike.message,
  };
}

export function extractApiErrorMessage(source: unknown): string {
  if (!source) {
    return '';
  }
  if (typeof source === 'string') {
    return source.trim();
  }
  if (typeof source === 'object') {
    const { data, message } = readAxiosLike(source);
    const fromResponseData = extractFromRecord(asErrorPayload(data));
    if (fromResponseData) {
      return fromResponseData;
    }
    if (typeof message === 'string' && message.trim()) {
      return message.trim();
    }
    return extractFromRecord(source as ErrorPayload);
  }
  return String(source);
}

export function extractRemoveSupplierErrorDetail(source: unknown): string {
  if (!source) {
    return '';
  }
  if (typeof source === 'string') {
    return source.trim();
  }
  if (typeof source !== 'object') {
    return String(source);
  }

  const { status, data, message } = readAxiosLike(source);
  const payload = asErrorPayload(data);
  const fromJson = extractFromRecord(payload);
  if (fromJson) {
    return fromJson;
  }

  if (data !== undefined && data !== null) {
    return truncateDetail(formatHttpStatusAndBody(status, data));
  }

  if (status !== undefined) {
    return String(status);
  }

  if (typeof message === 'string' && message.trim()) {
    return message.trim();
  }

  return extractFromRecord(source as ErrorPayload);
}

const NO_ERROR_DETAILS = 'сервис не вернул описание ошибки';

/**
 * Текст причины для тоста «Возникла ошибка при выполнении операции».
 *
 * Сюда прилетает либо строка от Task.readableError, либо Error из catch, либо
 * что угодно ещё. Раньше значение подставлялось в шаблон напрямую, и пустая
 * строка превращала тост в «попробуйте позже -» без причины: пользователю
 * сказать нечего, поддержке процитировать нечего (задача 10225).
 */
export function describeOperationError(source: unknown): string {
  const extracted = extractApiErrorMessage(source).trim();
  if (extracted && extracted !== '[object Object]') {
    return truncateDetail(extracted);
  }
  const dumped = stringifyBody(source);
  if (dumped && dumped !== '{}' && dumped !== '[]' && dumped !== '[object Object]') {
    return truncateDetail(dumped);
  }
  return NO_ERROR_DETAILS;
}

export function formatRemoveSupplierError(detail: string): string {
  const base = 'Не удалось снять поставщика в Pimcore';
  const trimmed = detail.trim();
  return trimmed ? `${base}: ${trimmed}` : base;
}
